import Vue from 'vue'
import VueRouter from 'vue-router'
import { isLoggedIn } from '@/utils/auth'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: {
      public: true,
      title: '登录',
      desc: '请输入后台账号和密码。'
    }
  },
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/DashboardPhase5.vue'),
    meta: {
      title: '首页',
      desc: '一屏确认系统当前能否完整分析，并快速进入第一本账、核心指数、结论留痕、任务监控、运行记录和模拟账户。'
    }
  },
  {
    path: '/first-book',
    name: 'FirstBook',
    component: () => import('@/views/FirstBookWorkspace.vue'),
    meta: {
      title: '第一本账',
      desc: '查看每日复核计划、待确认建议、分析数据状态，并维护正式区与临时区。'
    }
  },
  {
    path: '/simulation-account',
    name: 'SimulationAccount',
    component: () => import('@/views/SimulationAccount.vue'),
    meta: {
      title: '模拟账户',
      desc: '只读查看正式模拟账户的资金、当前持仓、完整交易、绩效曲线、策略复盘和账户流水。'
    }
  },
  {
    path: '/real-positions',
    name: 'RealPositions',
    component: () => import('@/views/RealPositions.vue'),
    meta: {
      title: '真实持仓',
      desc: '只读查看已确认的券商截图快照、历史持仓和快照时效。'
    }
  },
  {
    path: '/core-index-history',
    name: 'CoreIndexHistoryLibrary',
    component: () => import('@/views/CoreIndexHistoryLibrary.vue'),
    meta: {
      title: '核心指数',
      desc: '查看固定 7 个核心指数的日线、5分钟与派生周期分析数据状态。'
    }
  },
  {
    path: '/history-monitor',
    name: 'HistoryMonitor',
    component: () => import('@/views/HistoryMonitor.vue'),
    meta: {
      title: '数据健康',
      desc: '查看分析与行情数据是否可用，内部数据维护与运行细节由后台治理。'
    }
  },
  {
    path: '/scheduled-tasks',
    name: 'ScheduledTasks',
    component: () => import('@/views/TaskMonitorLightweight.vue'),
    meta: {
      title: '任务监控',
      desc: '查看四项固定任务是否启用、何时运行、最近结果和通知状态。'
    }
  },
  {
    path: '/scheduled-tasks/advanced',
    name: 'ScheduledTasksAdvanced',
    component: () => import('@/views/ScheduledTasksParentRuntime.vue'),
    meta: {
      title: '任务设置 · 高级',
      desc: '仅在修改任务计划或排障时使用。'
    }
  },
  {
    path: '/notifications',
    redirect: {
      path: '/scheduled-task-runs',
      query: { section: 'notifications' }
    }
  },
  {
    path: '/scheduled-task-runs',
    name: 'ScheduledTaskRuns',
    component: () => import('@/views/RunRecordsLightweight.vue'),
    meta: {
      title: '运行记录',
      desc: '查看任务结果、通知发送状态、失败说明和诊断编号。'
    }
  },
  {
    path: '/scheduled-task-runs/advanced',
    name: 'ScheduledTaskRunsAdvanced',
    component: () => import('@/views/ScheduledTaskRunsParentRuntime.vue'),
    meta: {
      title: '运行记录 · 高级明细',
      desc: '排障时查看父任务下的完整子模块编号和只读合同。'
    }
  },
  {
    path: '/logs',
    redirect: '/scheduled-task-runs'
  },
  {
    path: '/system-settings',
    name: 'SystemSettings',
    component: () => import('@/views/SystemSettingsLightweight.vue'),
    meta: {
      title: '系统设置',
      desc: '设置通知通道，并只读查看数据源状态。密钥和高级参数由 GPT 管理员维护。'
    }
  },
  {
    path: '/system-settings/advanced',
    name: 'SystemSettingsAdvanced',
    component: () => import('@/views/SystemSettings.vue'),
    meta: {
      title: '系统设置 · 管理员诊断',
      desc: 'GPT管理员只读核对工具权限、运行边界和服务状态；不提供密钥、模型或运行参数写入。'
    }
  },
  {
    path: '/conclusions',
    name: 'Conclusions',
    component: () => import('@/views/Conclusions.vue'),
    meta: {
      title: '结论留痕',
      desc: '查看分析结论、验证结果、log_id、request_id 和历史留痕。'
    }
  },
  {
    path: '/data-workbench',
    name: 'DataWorkbench',
    component: () => import('@/views/DataWorkbench.vue'),
    meta: {
      title: 'A股环境',
      desc: '降级入口：查看市场环境数据源状态。'
    }
  },
  {
    path: '/stock-kline',
    name: 'StockKline',
    component: () => import('@/views/StockKline.vue'),
    meta: {
      title: '个股数据',
      desc: '降级入口：只查看数据，不作为前端分析决策入口。'
    }
  },
  {
    path: '/chat-analysis',
    name: 'ChatAnalysis',
    component: () => import('@/views/ChatAnalysis.vue'),
    meta: {
      title: '聊天触发记录',
      desc: '只作为聊天触发后的数据载荷、状态、日志和排障入口。'
    }
  },
  {
    path: '/api-docs',
    name: 'ApiDocs',
    component: () => import('@/views/ApiDocs.vue'),
    meta: {
      title: '接口文档',
      desc: '开发模式入口：查看接口说明。'
    }
  }
]

const router = new VueRouter({
  mode: 'hash',
  routes
})

// 登录页和登录按钮保留，但在后端认证接口确认前不强制拦截后台页面。
router.beforeEach((to, from, next) => {
  if (to.path === '/login' && isLoggedIn()) {
    next('/dashboard')
    return
  }
  next()
})

export default router